/**
 * Carbon toolbar search (DESIGN.md "Inputs / Fields"): a full-height field
 * with a leading search icon and a border-strong underline, filling the
 * toolbar's free width (200px minimum; its own 48px row below c-md). The
 * label is for screen readers; the placeholder repeats it unless given.
 */
import type { Ref } from 'react'
import clsx from 'clsx'
import { IconSearch } from './icons'

export interface TableSearchProps {
  value: string
  onChange: (value: string) => void
  /** Accessible name ("Search settings"). */
  label: string
  placeholder?: string
  /** The input itself, for a page shortcut that focuses or blurs the search (Ctrl/Cmd+K, Escape). */
  inputRef?: Ref<HTMLInputElement>
  className?: string
}

export function TableSearch({ value, onChange, label, placeholder, inputRef, className }: TableSearchProps) {
  return (
    <label
      className={clsx(
        'flex min-h-12 min-w-[200px] flex-1 items-center gap-3 border-b border-line-strong bg-field px-4 text-[color:var(--icon-secondary)]',
        'focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-focus max-c-md:basis-full',
        className,
      )}
    >
      <IconSearch />
      <span className="sr-only">{label}</span>
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? label}
        autoComplete="off"
        className="h-[46px] min-w-0 flex-1 border-0 bg-transparent text-body text-text-primary placeholder:text-text-placeholder focus:outline-none focus-visible:outline-none"
      />
    </label>
  )
}

export default TableSearch
